import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../../../services/api';
import BookingDetail from './BookingDetail';
import '../Shared/FormStyles.scss';
import './BookingsManager.scss';

const BookingsManager = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    date: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchBookings();
  }, [filters]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.date) params.date = filters.date;
      
      const response = await bookingAPI.getAll(params);
      setBookings(response.data.bookings || []);
    } catch (err) {
      setError('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const response = await bookingAPI.update(id, { status: newStatus });
      const cancelledCount = response.data.cancelledDuplicates || 0;
      let msg = `Booking ${newStatus} successfully`;
      if (cancelledCount > 0) {
        msg += `. ${cancelledCount} duplicate booking${cancelledCount > 1 ? 's' : ''} for the same time slot ${cancelledCount > 1 ? 'were' : 'was'} auto-cancelled.`;
      }
      setSuccess(msg);
      fetchBookings();
      if (selectedBooking && selectedBooking._id === id) {
        setSelectedBooking({ ...selectedBooking, status: newStatus });
      }
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update booking status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      await bookingAPI.delete(id);
      setSuccess('Booking deleted successfully');
      if (selectedBooking && selectedBooking._id === id) {
        setSelectedBooking(null);
      }
      fetchBookings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete booking');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${period}`;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: '#f39c12',
      confirmed: '#27ae60',
      cancelled: '#e74c3c',
      completed: '#3498db',
    };
    
    return (
      <span
        className="status-badge"
        style={{ backgroundColor: statusColors[status] || '#95a5a6' }}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return <div className="loading-state">Loading bookings...</div>;
  }

  return (
    <div className="bookings-manager">
      <div className="admin-form">
        <div className="header-section">
          <h2>Bookings Management</h2>
          <div className="filters">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              className="filter-input"
              placeholder="Filter by date"
            />
            <button
              className="dashboard-btn btn-secondary"
              onClick={() => setFilters({ status: '', date: '' })}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="bookings-layout">
          <div className="bookings-list">
            {bookings.length === 0 ? (
              <div className="empty-state">No bookings found</div>
            ) : (
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Patient</th>
                      <th>Contact</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr
                        key={booking._id}
                        className={selectedBooking?._id === booking._id ? 'selected' : ''}
                        onClick={() => setSelectedBooking(booking)}
                      >
                        <td>{formatDate(booking.appointmentDate)}</td>
                        <td>{formatTime(booking.timeSlot)}</td>
                        <td>{booking.patientName}</td>
                        <td>
                          <div className="contact-info">
                            <div>{booking.email}</div>
                            <div className="phone">{booking.phone}</div>
                          </div>
                        </td>
                        <td>{getStatusBadge(booking.status)}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="btn-edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBooking(booking);
                              }}
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selectedBooking && (
            <BookingDetail
              booking={selectedBooking}
              onStatusUpdate={handleStatusUpdate}
              onDelete={handleDelete}
              onClose={() => setSelectedBooking(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingsManager;
